import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { MarkdownModule } from 'ngx-markdown';

import { BaseComponent } from 'src/app/@shared/components/abstract/base.component';
import { Announcement } from 'src/app/@shared/models/announcements/announcement.model';

export interface AnnouncementDialogData {
    announcement: Announcement;
}

/**
 * Affichage d'une annonce en fenêtre.
 *
 * Les annonces qui exigent un acquittement (mode critique, ou catégorie maintenance —
 * cf. `Announcement.requiresAcknowledgement`) sont ouvertes avec la configuration
 * `ANNOUNCEMENT_ACKNOWLEDGE_DIALOG`, qui retire la croix, l'échappement et le masque : le
 * bouton « J'ai compris » est le seul moyen d'en sortir. Aucun traitement de couleur ne
 * les distingue — seuls le relief du bouton et l'impossibilité de fermer.
 */
@Component({
    selector: 'app-announcement-dialog',
    imports: [
        CommonModule,
        ButtonModule,
        TagModule,
        MarkdownModule,
    ],
    templateUrl: './announcement-dialog.component.html'
})
export class AnnouncementDialogComponent extends BaseComponent {

    private _config = inject(DynamicDialogConfig);
    private _ref = inject(DynamicDialogRef);

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    public acknowledging: boolean = false;

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    public get acknowledgeLabel(): string {
        return this.announcement.requiresAcknowledgement ? 'J\'ai compris' : 'Fermer';
    }

    public get announcement(): Announcement {
        return this.data.announcement;
    }

    public get data(): AnnouncementDialogData {
        return this._config.data;
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public onClickAcknowledge(): void {
        this._acknowledge();
    }

    public onClickClose(): void {
        this._close();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * L'acquittement vaut lecture : c'est le front qui décide du moment de l'appel, le
     * serveur n'observe rien de lui-même.
     */
    private _acknowledge(): void {
        this.acknowledging = true;
        this.announcementService.markAsRead$(this.announcement).subscribe({
            next: () => {
                this.acknowledging = false;
                this._ref.close({ read: true });
            },
            error: () => {
                this.acknowledging = false;
                this._ref.close({ read: false });
            },
        });
    }

    /**
     * Fermeture sans acquitter. Le marquage reste au shell, qui l'applique à la fermeture
     * de la fenêtre quel qu'en soit le moyen — comme le faisait la croix native.
     */
    private _close(): void {
        this._ref.close({ read: false });
    }
}
